// 부가서비스·보험처럼 "여러 개 선택"으로 바뀐 신청서 항목의 저장값 ↔ 화면값 변환.
//
// 저장 형태(신청서 retailer_info jsonb): 일반 다중 항목은 이름만 저장한다. 대리점 부가서비스의
// 가입 목록은 선택 당시 금액까지 {name, monthlyFee, feeNote}로 저장해 과거 신청서 금액을 고정한다.
//
// 유지기간(가입 부가서비스)은 더는 신청서마다 저장하지 않는다. 부가서비스 마스터
// (addon_masters.required_expr)에 "M+2"·"D+95" 같은 식을 한 번 정해 두면, 그 부가서비스를
// 쓰는 모든 신청서가 그 식을 따른다. 해지가능일은 화면에 보일 때마다 "각 신청서의 개통일 +
// 마스터 식"으로 계산한다(식 하나, 결과는 신청서별 개통일에 맞게). 그래서 변환 함수들은
// 이름만 다루고, 유지기간 식은 호출부가 마스터에서 풀어(resolver) 넘겨준다.
//
// 하위호환: 과거엔 [{ name, months, expr? }] 객체배열이나 단일 문자열("LTE안심")로 저장됐다.
// 불러올 때 이름만 뽑아 목록으로 변환한다(옛 months·expr 은 무시 — 마스터가 출처).

import type { SelectedMasterItem } from "../components/crm/MultiMasterCombobox";
import { addonFeeChipLabel } from "./addonFeeLabel";

/** 저장값(문자열 | 문자열배열 | 객체배열) → 화면 선택 목록. */
export function parseMultiItems(raw: unknown): SelectedMasterItem[] {
  if (raw == null || raw === "") return [];
  if (typeof raw === "string") return [{ name: raw }];
  if (Array.isArray(raw)) {
    const out: SelectedMasterItem[] = [];
    for (const el of raw) {
      if (typeof el === "string") {
        if (el.trim() !== "") out.push({ name: el });
      } else if (el && typeof el === "object" && "name" in el) {
        // 옛 객체 형태({name, months, expr})도 받아들이되, 새 금액 스냅샷이 있으면 보존한다.
        const record = el as Record<string, unknown>;
        const name = String(record.name ?? "").trim();
        if (!name) continue;
        out.push({
          name,
          ...(typeof record.monthlyFee === "number"
            ? { monthlyFee: record.monthlyFee }
            : {}),
          ...(record.feeNote === null || typeof record.feeNote === "string"
            ? { feeNote: record.feeNote }
            : {}),
        });
      }
    }
    return out;
  }
  return [];
}

/** 선택 항목 → 이름 문자열 배열. 부가서비스·보험 모두 이름만 저장한다. */
export function serializeNames(items: SelectedMasterItem[]): string[] {
  return items.map((i) => i.name);
}

/** 대리점 부가서비스 가입 목록은 선택 당시 금액과 함께 저장한다. */
export function serializeAddonSelections(
  items: SelectedMasterItem[],
): Array<string | SelectedMasterItem> {
  return items.map((item) =>
    typeof item.monthlyFee === "number"
      ? {
          name: item.name,
          monthlyFee: item.monthlyFee,
          feeNote: item.feeNote ?? null,
        }
      : item.name,
  );
}

/** 신청서 가입 목록의 저장 금액을 이름별로 푼다. */
export function addonFeeSnapshots(raw: unknown): Map<string, SelectedMasterItem> {
  return new Map(
    parseMultiItems(raw)
      .filter((item) => typeof item.monthlyFee === "number")
      .map((item) => [item.name, item]),
  );
}

/** 부가서비스 마스터의 유지기간 정보(식 또는 옛 유지개월). 호출부가 이름으로 풀어 넘긴다. */
export interface AddonRetention {
  expr?: string | null;
  months?: number | null;
  /** 대리점 부가서비스에 현재 남아 있는 항목만 값이 있다. 없으면 옛 삭제 항목이라 이름만 표시. */
  monthlyFee?: number;
  feeNote?: string | null;
}

// ────────────────────────────────────────────────────────────────────────
// 유지기간 계산식 → 해지가능일
//
// 입력 규칙
//   · M+N : 개통월을 기준으로 N개월 뒤가 되는 달의 "말일"까지 유지하고, 그 다음날.
//           예) 개통일 5/26, M+1 → 6월 말일(6/30)까지 유지 → +1일 → 7/1.
//           (개통일의 "일"은 무시하고 달 단위로만 센다.)
//   · D+N : 개통일에 N일을 그대로 더한다.
//           예) 개통일 5/26, D+1 → 5/27.
// ────────────────────────────────────────────────────────────────────────

export interface RetentionCalc {
  /** 식이 비어 있으면 false(에러 아님). 형식 오류면 false + error. */
  ok: boolean;
  /** 계산된 해지가능일. ok 일 때만 채워진다. */
  date?: Date;
  /** "2025-07-01 (화)" 같은 표시용 문자열. */
  label?: string;
  /** 형식이 틀렸을 때 안내 문구. */
  error?: string;
}

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

/** 개통일 문자열("2025-05-26" 또는 "2025-05-26T14:30")에서 날짜만 뽑아 로컬 Date 로. */
export function parseBaseDate(baseISO: string | null | undefined): Date | null {
  if (!baseISO) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(baseISO));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Date → "YYYY-MM-DD (요일)". */
function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da} (${WEEKDAYS[d.getDay()]})`;
}

/**
 * 유지기간 계산식을 개통일 기준 해지가능일로 변환.
 * 개통일이 비어 있으면 오늘을 기준으로 계산한다(usedToday=true).
 */
export function computeRetentionDate(
  expr: string | null | undefined,
  baseISO: string | null | undefined,
): RetentionCalc & { usedToday?: boolean } {
  const norm = (expr ?? "").toUpperCase().replace(/\s+/g, "");
  if (norm === "") return { ok: false };
  const m = /^([MD])\+(\d+)$/.exec(norm);
  if (!m) {
    return {
      ok: false,
      error: "형식: M+숫자 또는 D+숫자 (예: M+1, D+10)",
    };
  }
  const kind = m[1];
  const n = Number(m[2]);
  const parsed = parseBaseDate(baseISO);
  const usedToday = parsed == null;
  const base = parsed ?? new Date();

  let result: Date;
  if (kind === "M") {
    // 개통월 + N개월째 달의 말일 + 1일 = (개통월 + N + 1)월 1일.
    // JS 의 월 인덱스는 0-기반이라 12를 넘어가면 연도가 자동으로 넘어간다.
    result = new Date(base.getFullYear(), base.getMonth() + n + 1, 1);
  } else {
    // D+N: 개통일에 N일을 그대로 더한다(+1 보정 없음).
    result = new Date(base.getFullYear(), base.getMonth(), base.getDate() + n);
  }
  return { ok: true, date: result, label: formatYmd(result), usedToday };
}

/**
 * 유지기간 식을 고객이 읽는 말로 — "M+2"→"당월 +2개월 추가 유지", "D+95"→"당일 +95일 유지".
 * 식이 비었거나 M+N/D+N 형식이 아니면 null(호출부는 표시를 생략한다 — 깨진 표기 방지).
 */
export function retentionExprHumanLabel(expr: string | null | undefined): string | null {
  const norm = (expr ?? "").toUpperCase().replace(/\s+/g, "");
  const m = /^([MD])\+(\d+)$/.exec(norm);
  if (!m) return null;
  return m[1] === "M"
    ? `당월 +${Number(m[2])}개월 추가 유지`
    : `당일 +${Number(m[2])}일 유지`;
}

/**
 * 표시용 한 줄 문자열. withRetention 이면 가입 부가서비스로 보고, 유지기간은 마스터에서
 * 풀어 넘긴 resolveRetention(name) 으로 계산한다(마스터가 단일 출처).
 *   · 대리점 목록에 있으면 이름 뒤에 월 사용료(0·미입력은 "월 0원")
 *   · 유지기간 식이 있으면 월 사용료 뒤에 계산된 해지가능일
 *   · 대리점 목록에서 이미 지운 옛 항목이면 resolver 가 undefined → 이름만
 * resolveRetention 이 없으면 이름만 나열한다.
 */
export function formatMultiItems(
  raw: unknown,
  withRetention: boolean,
  baseISO?: string | null,
  resolveRetention?: (name: string) => AddonRetention | undefined,
): string {
  const items = parseMultiItems(raw);
  if (items.length === 0) return "—";
  if (!resolveRetention)
    return items.map((i) => i.name).join(", ");
  return items
    .map((i) => {
      const r = resolveRetention(i.name);
      if (!r) return i.name;
      const fee =
        r.monthlyFee === undefined
          ? ""
          : addonFeeChipLabel(r.monthlyFee, r.feeNote);
      const prefix = [i.name, fee].filter(Boolean).join(" ");
      if (withRetention && r.expr) {
        const calc = computeRetentionDate(r.expr, baseISO);
        if (calc.ok && calc.label) {
          const ymd = calc.label.split(" ")[0]; // 요일 괄호 떼고 날짜만
          return `${prefix} (${ymd})`;
        }
        return prefix;
      }
      if (withRetention && r.months && r.months > 0)
        return `${prefix} (${r.months}개월)`;
      return prefix;
    })
    .join(", ");
}
